import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { taxonomyService } from '@services/taxonomy.service';
import {
  apiErrorDetail,
  apiErrorStatus,
  resolveApiErrorMessage,
} from '@utils/apiErrorMessages';
import type {
  DocumentTaxonomy,
  ReviewChange,
  TaxonomyAssignment,
  TaxonomyRefusalCode,
} from '@/types/taxonomy';

// The key arrives from a response body, so the table is null-prototype: a plain
// literal would answer 'constructor' / 'toString' off Object.prototype.
const closed = (rows: Record<string, string>): Readonly<Record<string, string>> =>
  Object.assign(Object.create(null) as Record<string, string>, rows);

// ANNOTATED, not cast: a cast would accept both a missing code and a stale one.
// This way a refusal the API can send and this table lacks is a compile error,
// and so is a row for a code the API no longer has.
const REFUSAL_ROWS: Record<TaxonomyRefusalCode, string> = {
  unknown_code: 'unknownCode',
  deprecated_term: 'deprecatedTerm',
  no_active_taxonomy: 'noActiveTaxonomy',
  duplicate_change: 'duplicateChange',
  cardinality_violation: 'cardinalityViolation',
  migrated_not_confirmable: 'migratedNotConfirmable',
  term_no_longer_available: 'termNoLongerAvailable',
  not_assigned: 'notAssigned',
  confirm_not_applied: 'confirmNotApplied',
};

const REFUSAL_KEYS = closed(REFUSAL_ROWS);

const RETRY_REFUSAL_KEYS = closed({
  already_succeeded: 'alreadySucceeded',
  run_in_progress: 'runInProgress',
});

/** Which verb a row has in flight, so its two buttons report different things. */
export type BusyVerb = 'confirm' | 'reject';

/** One shared empty map, so an idle panel does not get a new object per render. */
const EMPTY_BUSY: Readonly<Record<string, BusyVerb>> = Object.freeze(
  Object.create(null) as Record<string, BusyVerb>,
);

export interface TaxonomyReviewState {
  taxonomy: DocumentTaxonomy | null;
  unavailable: boolean;
  error: string | null;
  notice: string | null;
  busy: Readonly<Record<string, BusyVerb>>;
  confirm: (assignment: TaxonomyAssignment) => Promise<void>;
  reject: (assignment: TaxonomyAssignment) => Promise<void>;
  retry: () => Promise<void>;
  retrying: boolean;
}

/** Any value this hook holds belongs to one document and is dropped with it. */
interface Owned<T> {
  id: string;
  value: T;
}

export function useTaxonomyReview(
  documentId: string | undefined,
  enabled = true,
): TaxonomyReviewState {
  const { t } = useTranslation();
  // `t` is read through a ref so it stays out of `read`'s identity: the load
  // effect depends on `read`, and a language switch would otherwise refetch the
  // whole taxonomy. The cost is that a message already on screen keeps the
  // language it was written in until the next action.
  const translate = useRef(t);
  translate.current = t;

  // Everything is keyed by document. `/documents/:id` changing does not remount
  // this page, so an unkeyed value would leave the previous document's rows and
  // messages on screen — and a press on one of those rows would send its code
  // against the NEW document.
  const [loaded, setLoaded] = useState<Owned<DocumentTaxonomy> | null>(null);
  const [absent, setAbsent] = useState<Owned<true> | null>(null);
  const [failure, setFailure] = useState<Owned<string> | null>(null);
  const [message, setMessage] = useState<Owned<string> | null>(null);
  const [busyRows, setBusyRows] = useState<Owned<Record<string, BusyVerb>> | null>(null);
  const [retrying, setRetrying] = useState(false);

  const mine = <T, >(held: Owned<T> | null): T | null => (
    held && held.id === documentId ? held.value : null
  );
  const taxonomy = mine(loaded);
  const unavailable = mine(absent) === true;
  const error = mine(failure);
  const notice = mine(message);
  const busyHeld = mine(busyRows);

  // Reads get their OWN counter, so a refetch cancels only other reads. Writes
  // are guarded by document identity instead: a review that answers late is
  // still about this document and its outcome must not be discarded.
  const reads = useRef(0);
  const current = useRef(documentId);
  current.current = documentId;

  const read = useCallback(async (): Promise<DocumentTaxonomy | null> => {
    if (!documentId) return null;
    const slot = ++reads.current;
    setFailure(null);
    setMessage(null);
    try {
      const answer = await taxonomyService.getDocumentTaxonomy(documentId);
      if (slot !== reads.current) return null;
      setLoaded({ id: documentId, value: answer });
      setAbsent(null);
      return answer;
    } catch (caught) {
      if (slot !== reads.current) return null;
      // 404 is the feature being off for this tenant, or a document that is not
      // ours — indistinguishable on purpose, and nothing to show either way.
      const gone = apiErrorStatus(caught) === 404;
      setAbsent(gone ? { id: documentId, value: true } : null);
      setFailure(gone ? null : {
        id: documentId,
        value: resolveApiErrorMessage(translate.current, caught, {
          fallbackKey: 'metadata.taxonomy.loadFailed',
        }),
      });
      return null;
    }
    // No `loading` flag: nothing renders one. The panel shows nothing at all
    // until the first answer arrives, so a spinner has no place to go, and
    // untested state that no consumer reads is state that will drift.
  }, [documentId]);

  useEffect(() => {
    if (!enabled) return;
    void read();
  }, [enabled, read]);

  const refusalMessage = useCallback((caught: unknown): string | null => {
    const code = apiErrorDetail(caught)?.error;
    const key = typeof code === 'string' ? REFUSAL_KEYS[code] : undefined;
    return key ? translate.current(`metadata.taxonomy.refusal.${key}`) : null;
  }, []);

  const applyChanges = useCallback(
    async (termId: string, verb: BusyVerb, changes: ReviewChange[]) => {
      if (!documentId) return;
      const owner = documentId;
      setBusyRows((held) => ({
        id: owner,
        value: { ...(held && held.id === owner ? held.value : {}), [termId]: verb },
      }));
      setFailure(null);
      setMessage(null);
      try {
        const updated = await taxonomyService.review(owner, changes);
        // Only the document still on screen; a later read cannot invalidate this
        // answer, because both describe the same document and this one is newer.
        if (current.current === owner) setLoaded({ id: owner, value: updated });
      } catch (caught) {
        // ALWAYS reported. Gating this on a request counter dropped the refusal
        // whenever a second row was pressed first, and the row went back to
        // looking un-decided with no message anywhere (invariant 4).
        if (current.current === owner) {
          setFailure({
            id: owner,
            value: refusalMessage(caught) ?? resolveApiErrorMessage(
              translate.current, caught, { fallbackKey: 'metadata.taxonomy.reviewFailed' },
            ),
          });
        }
      } finally {
        setBusyRows((held) => {
          if (!held || held.id !== owner) return held;
          const { [termId]: _done, ...rest } = held.value;
          return { id: owner, value: rest };
        });
      }
    },
    [documentId, refusalMessage],
  );

  const decide = useCallback(
    async (verb: BusyVerb, assignment: TaxonomyAssignment) => {
      // No code means no way to name this term in a change; the panel offers no
      // button for such a row, so reaching here at all is a caller bug.
      if (!assignment.code) return;
      await applyChanges(assignment.termId, verb, [
        { action: verb, facet: assignment.facet, code: assignment.code },
      ]);
    },
    [applyChanges],
  );

  const confirm = useCallback(
    (assignment: TaxonomyAssignment) => decide('confirm', assignment), [decide],
  );
  const reject = useCallback(
    (assignment: TaxonomyAssignment) => decide('reject', assignment), [decide],
  );

  const retry = useCallback(async () => {
    if (!documentId) return;
    const owner = documentId;
    setRetrying(true);
    setFailure(null);
    setMessage(null);
    try {
      const run = await taxonomyService.retrySuggestions(owner);
      if (current.current !== owner) return;
      setLoaded((held) => (held && held.id === owner
        ? { id: owner, value: { ...held.value, suggestionRun: run } }
        : held));
      setMessage({ id: owner, value: translate.current('metadata.taxonomy.retry.started') });
    } catch (caught) {
      if (current.current !== owner) return;
      const status = apiErrorStatus(caught);
      if (status === 409) {
        // 409 is an answer, not a fault, and the server names WHICH answer.
        const named = apiErrorDetail(caught)?.error;
        const fresh = await read();
        if (current.current !== owner) return;
        const key = RETRY_REFUSAL_KEYS[String(named)]
          // No named reason: fall back to the run the reload returned.
          ?? (fresh
            ? (fresh.suggestionRun.status === 'succeeded' ? 'alreadySucceeded' : 'runInProgress')
            : undefined);
        if (key) {
          setMessage({ id: owner, value: translate.current(`metadata.taxonomy.retry.${key}`) });
          // Only when the reload actually answered. Clearing unconditionally
          // erased the error that same reload had just reported.
          if (fresh) setFailure(null);
        }
      } else {
        setFailure({
          id: owner,
          value: status === 403
            ? translate.current('metadata.taxonomy.retry.forbidden')
            : resolveApiErrorMessage(translate.current, caught, {
              fallbackKey: 'metadata.taxonomy.retry.failed',
            }),
        });
      }
    } finally {
      setRetrying(false);
    }
  }, [documentId, read]);

  return useMemo(() => ({
    taxonomy,
    unavailable,
    error,
    notice,
    // Inside the memo: a `?? {}` fallback outside it is a fresh object on every
    // render, which would make this memo pointless.
    busy: busyHeld ?? EMPTY_BUSY,
    confirm,
    reject,
    retry,
    retrying,
  }), [taxonomy, unavailable, error, notice, busyHeld,
    confirm, reject, retry, retrying]);
}
